import { useEffect, useState, useCallback } from 'react';
import { get } from '../../lib/api-client';

interface AuditEvent {
  id: string;
  userId: string | null;
  teamId: string | null;
  action: string;
  resourceType: string;
  resourceId: string | null;
  metadata: Record<string, unknown>;
  ipAddress: string | null;
  createdAt: string;
}

interface AuditResponse {
  data: AuditEvent[];
  pagination: { page: number; pageSize: number; total: number };
}

export function Audit() {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [eventType, setEventType] = useState('');
  const [userId, setUserId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pageSize = 25;

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      if (eventType) params.set('eventType', eventType);
      if (userId) params.set('userId', userId);
      const data = await get<AuditResponse>(`/v1/admin/audit?${params.toString()}`);
      setEvents(data.data);
      setTotal(data.pagination.total);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load audit events');
    } finally {
      setLoading(false);
    }
  }, [page, eventType, userId]);

  useEffect(() => {
    void fetchAudit();
  }, [fetchAudit]);

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-ll-text">Audit Log</h2>

      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          value={eventType}
          onChange={(e) => {
            setEventType(e.target.value);
            setPage(1);
          }}
          placeholder="Filter by event type..."
          className="flex-1 bg-ll-bg border border-ll-border rounded-lg px-3 py-2 text-sm text-ll-text placeholder-ll-muted/50 focus:outline-none focus:ring-2 focus:ring-ll-cyan"
        />
        <input
          type="text"
          value={userId}
          onChange={(e) => {
            setUserId(e.target.value);
            setPage(1);
          }}
          placeholder="Filter by user ID..."
          className="flex-1 bg-ll-bg border border-ll-border rounded-lg px-3 py-2 text-sm text-ll-text placeholder-ll-muted/50 focus:outline-none focus:ring-2 focus:ring-ll-cyan"
        />
      </div>

      {error && (
        <div className="bg-ll-error/10 border border-ll-error/30 rounded-lg p-4 text-sm text-ll-error">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-ll-muted">Loading...</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ll-border text-left">
                  <th className="py-2 px-3 text-xs uppercase font-mono text-ll-muted">Time</th>
                  <th className="py-2 px-3 text-xs uppercase font-mono text-ll-muted">Action</th>
                  <th className="py-2 px-3 text-xs uppercase font-mono text-ll-muted">Resource</th>
                  <th className="py-2 px-3 text-xs uppercase font-mono text-ll-muted">User</th>
                  <th className="py-2 px-3 text-xs uppercase font-mono text-ll-muted">IP</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr key={event.id} className="border-b border-ll-border/50 hover:bg-white/[0.02]">
                    <td className="py-2 px-3 text-ll-muted text-xs whitespace-nowrap">
                      {new Date(event.createdAt).toLocaleString()}
                    </td>
                    <td className="py-2 px-3">
                      <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-ll-cyan/10 text-ll-cyan">
                        {event.action}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-ll-text text-xs">
                      {event.resourceType}
                      {event.resourceId && (
                        <span className="text-ll-muted"> ({event.resourceId})</span>
                      )}
                    </td>
                    <td className="py-2 px-3 text-ll-muted text-xs font-mono">
                      {event.userId ?? '--'}
                    </td>
                    <td className="py-2 px-3 text-ll-muted text-xs font-mono">
                      {event.ipAddress ?? '--'}
                    </td>
                  </tr>
                ))}
                {events.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-ll-muted">
                      No audit events found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-ll-muted">{total} events total</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="px-3 py-1.5 rounded border border-ll-border text-xs text-ll-muted hover:text-ll-text disabled:opacity-30 transition-colors"
                >
                  Previous
                </button>
                <span className="text-xs text-ll-muted py-1.5">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="px-3 py-1.5 rounded border border-ll-border text-xs text-ll-muted hover:text-ll-text disabled:opacity-30 transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
